/**
 * Advanced Analytics Engine
 * Decline Curve Analysis, EUR, ROI, Predictive Maintenance, Production Forecasting
 */

export interface ProductionDataPoint {
  date: Date;
  oil: number; // barrels
  gas: number; // MCF
  water: number; // barrels
  days: number; // producing days
}

export interface DeclineCurveAnalysis {
  wellId: string;
  wellName: string;
  model: 'exponential' | 'hyperbolic' | 'harmonic';
  
  // Historical data
  historical: Array<{
    date: Date;
    time: number; // days since start
    rate: number; // bbl/day
    cumulative: number; // barrels
  }>;
  
  // Fitted parameters
  parameters: {
    qi: number; // Initial rate (bbl/day)
    di: number; // Initial decline rate (1/day or 1/year)
    b: number; // Hyperbolic exponent (0 = exponential, 1 = harmonic)
    ti: number; // Start time (days)
  };
  
  // Forecast
  forecast: Array<{
    date: Date;
    time: number; // days
    rate: number; // bbl/day
    cumulative: number; // barrels
  }>;
  
  // Quality metrics
  quality: {
    r2: number; // R-squared (0-1, higher is better)
    rmse: number; // Root mean square error
    confidence: 'high' | 'medium' | 'low';
  };
  
  // Economic limit
  economicLimit: {
    rate: number; // bbl/day
    date: Date;
    cumulativeProduction: number;
  };
}

export interface EURCalculation {
  wellId: string;
  wellName: string;
  
  // Oil EUR
  oil: {
    eur: number; // barrels
    produced: number; // barrels to date
    remaining: number; // barrels
    percentRecovered: number; // %
  };
  
  // Gas EUR
  gas: {
    eur: number; // MCF
    produced: number; // MCF to date
    remaining: number; // MCF
    percentRecovered: number; // %
  };
  
  // Based on decline curve
  method: 'decline-curve' | 'volumetric' | 'analogy';
  confidence: 'high' | 'medium' | 'low';
}

export interface ROIDashboard {
  wellId: string;
  wellName: string;
  
  // Investment
  investment: {
    drilling: number;
    completion: number;
    facilities: number;
    equipment: number;
    total: number;
  };
  
  // Revenue
  revenue: {
    historical: number;
    forecast: number;
    total: number;
  };
  
  // Operating Costs
  opex: {
    historical: number;
    forecast: number;
    total: number;
  };
  
  // Cash Flow
  cashFlow: Array<{
    date: Date;
    revenue: number;
    costs: number;
    netCashFlow: number;
    cumulativeCashFlow: number;
  }>;
  
  // Metrics
  metrics: {
    npv: number; // Net Present Value
    irr: number; // Internal Rate of Return (%)
    roi: number; // Return on Investment (%)
    paybackMonths: number;
    breakEvenDate: Date | null;
    profitabilityIndex: number;
  };
  
  discountRate: number; // %
}

export interface PredictiveMaintenance {
  wellId: string;
  wellName: string;
  
  // Risk Score (0-100)
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  
  // Contributing Factors
  factors: Array<{
    factor: string;
    weight: number; // 0-1
    value: number; // Current value
    threshold: number; // Alert threshold
    status: 'normal' | 'warning' | 'critical';
    contribution: number; // % contribution to risk score
  }>;
  
  // Predictions
  predictions: {
    nextFailureDate: Date | null;
    confidence: number; // 0-1
    recommendedAction: string;
    estimatedCost: number;
  };
  
  // Historical Issues
  issues: Array<{
    date: Date;
    type: string;
    cost: number;
    downtime: number; // hours
  }>;
}

export interface WaterCutAnalysis {
  wellId: string;
  wellName: string;
  
  // Historical water cut
  historical: Array<{
    date: Date;
    waterCut: number; // %
    oilRate: number; // bbl/day
    waterRate: number; // bbl/day
  }>;
  
  // Trend analysis
  trend: {
    direction: 'increasing' | 'decreasing' | 'stable';
    rate: number; // % change per month
    projection12Mo: number; // Projected water cut in 12 months
  };
  
  // Forecast
  forecast: Array<{
    date: Date;
    waterCut: number; // %
    confidence: number; // 0-1
  }>;
  
  // Thresholds
  thresholds: {
    current: number; // %
    warning: number; // % (e.g., 80%)
    critical: number; // % (e.g., 95%)
  };
}

export interface ProductionForecast {
  wellId: string;
  wellName: string;
  
  // 24-month forecast
  forecast: Array<{
    month: number;
    date: Date;
    oil: number; // barrels
    gas: number; // MCF
    revenue: number; // $
  }>;
  
  // Confidence intervals
  confidence: {
    level: number; // % (e.g., 95%)
    upper: number[]; // Upper bound for each month
    lower: number[]; // Lower bound for each month
  };
  
  // Assumptions
  assumptions: {
    oilPrice: number;
    gasPrice: number;
    operatingDays: number; // per month
    declineRate: number; // %
  };
}

class AnalyticsEngine {
  // ============= DECLINE CURVE ANALYSIS =============
  
  /**
   * Perform decline curve analysis on production data
   */
  analyzeDeclineCurve(
    wellId: string,
    wellName: string,
    productionData: ProductionDataPoint[],
    model: 'exponential' | 'hyperbolic' | 'harmonic' = 'hyperbolic',
    forecastMonths: number = 24
  ): DeclineCurveAnalysis {
    // Sort by date
    const sorted = [...productionData].sort((a, b) => a.date.getTime() - b.date.getTime());
    
    if (sorted.length < 3) {
      throw new Error('Need at least 3 data points for decline curve analysis');
    }
    
    // Convert to time series (days since first production)
    const startDate = sorted[0].date;
    const timeSeries = sorted.map(d => ({
      date: d.date,
      time: Math.floor((d.date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)),
      rate: d.days > 0 ? d.oil / d.days : 0, // bbl/day
      cumulative: 0,
    }));
    
    // Calculate cumulative production
    let cumulative = 0;
    timeSeries.forEach(point => {
      cumulative += point.rate * 30; // Approximate monthly
      point.cumulative = cumulative;
    });
    
    // Fit decline curve model
    const parameters = this.fitDeclineCurve(timeSeries, model);
    
    // Generate forecast
    const lastTime = timeSeries[timeSeries.length - 1].time;
    const forecast: any[] = [];
    
    for (let month = 1; month <= forecastMonths; month++) {
      const time = lastTime + (month * 30); // Days
      const rate = this.calculateDeclineRate(time, parameters, model);
      const date = new Date(startDate);
      date.setDate(date.getDate() + time);
      
      forecast.push({
        date,
        time,
        rate,
        cumulative: cumulative + (rate * 30 * month), // Approximate
      });
    }
    
    // Calculate quality metrics
    const quality = this.calculateFitQuality(timeSeries, parameters, model);
    
    // Determine economic limit (e.g., 5 bbl/day)
    const economicLimitRate = 5;
    const economicLimitIndex = forecast.findIndex(f => f.rate < economicLimitRate);
    const economicLimit = economicLimitIndex >= 0
      ? {
          rate: economicLimitRate,
          date: forecast[economicLimitIndex].date,
          cumulativeProduction: forecast[economicLimitIndex].cumulative,
        }
      : {
          rate: economicLimitRate,
          date: forecast[forecast.length - 1].date,
          cumulativeProduction: forecast[forecast.length - 1].cumulative,
        };
    
    return {
      wellId,
      wellName,
      model,
      historical: timeSeries,
      parameters,
      forecast,
      quality,
      economicLimit,
    };
  }
  
  /**
   * Fit decline curve parameters using least squares
   */
  private fitDeclineCurve(
    data: Array<{ time: number; rate: number }>,
    model: 'exponential' | 'hyperbolic' | 'harmonic'
  ): DeclineCurveAnalysis['parameters'] {
    // Simplified parameter estimation
    // In production, use proper non-linear regression
    
    const qi = data[0].rate; // Initial rate
    const ti = data[0].time; // Start time
    
    // Estimate decline rate using first and last points
    const tf = data[data.length - 1].time;
    const qf = data[data.length - 1].rate;
    
    let di = 0;
    let b = 0;
    
    if (model === 'exponential') {
      // q(t) = qi * exp(-di * t)
      // ln(qf/qi) = -di * t
      di = -Math.log(qf / qi) / (tf - ti) * 365; // Convert to 1/year
      b = 0;
    } else if (model === 'hyperbolic') {
      // q(t) = qi / (1 + b * di * t)^(1/b)
      // Use b = 0.5 as initial guess
      b = 0.5;
      di = (1 / b) * (Math.pow(qi / qf, b) - 1) / (tf - ti) * 365;
    } else {
      // Harmonic: b = 1
      b = 1;
      di = (qi / qf - 1) / (tf - ti) * 365;
    }
    
    return { qi, di: Math.max(0, di), b, ti };
  }
  
  /**
   * Calculate decline rate at time t
   */
  private calculateDeclineRate(
    t: number,
    params: DeclineCurveAnalysis['parameters'],
    model: 'exponential' | 'hyperbolic' | 'harmonic'
  ): number {
    const { qi, di, b, ti } = params;
    const dt = (t - ti) / 365; // Convert to years
    
    if (model === 'exponential') {
      // q(t) = qi * exp(-di * t)
      return qi * Math.exp(-di * dt);
    } else if (model === 'hyperbolic') {
      // q(t) = qi / (1 + b * di * t)^(1/b)
      return qi / Math.pow(1 + b * di * dt, 1 / b);
    } else {
      // Harmonic: q(t) = qi / (1 + di * t)
      return qi / (1 + di * dt);
    }
  }
  
  /**
   * Calculate R-squared and RMSE
   */
  private calculateFitQuality(
    data: Array<{ time: number; rate: number }>,
    params: DeclineCurveAnalysis['parameters'],
    model: 'exponential' | 'hyperbolic' | 'harmonic'
  ): DeclineCurveAnalysis['quality'] {
    // Calculate predicted values
    const predicted = data.map(d => this.calculateDeclineRate(d.time, params, model));
    
    // Calculate mean
    const mean = data.reduce((sum, d) => sum + d.rate, 0) / data.length;
    
    // Calculate R-squared
    const ssTot = data.reduce((sum, d) => sum + Math.pow(d.rate - mean, 2), 0);
    const ssRes = data.reduce((sum, d, i) => sum + Math.pow(d.rate - predicted[i], 2), 0);
    const r2 = 1 - (ssRes / ssTot);
    
    // Calculate RMSE
    const rmse = Math.sqrt(ssRes / data.length);
    
    // Determine confidence
    let confidence: 'high' | 'medium' | 'low';
    if (r2 > 0.9) {
      confidence = 'high';
    } else if (r2 > 0.7) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }
    
    return { r2, rmse, confidence };
  }
  
  // ============= EUR CALCULATION =============
  
  /**
   * Calculate Estimated Ultimate Recovery
   */
  calculateEUR(
    wellId: string,
    wellName: string,
    declineCurve: DeclineCurveAnalysis,
    currentProduction: { oil: number; gas: number }
  ): EURCalculation {
    // Integrate decline curve to economic limit
    const economicLimitTime = declineCurve.economicLimit.cumulativeProduction;
    
    // Oil EUR
    const oilEUR = economicLimitTime;
    const oilProduced = currentProduction.oil;
    const oilRemaining = Math.max(0, oilEUR - oilProduced);
    const oilPercentRecovered = oilEUR > 0 ? (oilProduced / oilEUR) * 100 : 0;
    
    // Gas EUR (assume gas-oil ratio of 1000:1)
    const gasOilRatio = 1000;
    const gasEUR = oilEUR * gasOilRatio;
    const gasProduced = currentProduction.gas;
    const gasRemaining = Math.max(0, gasEUR - gasProduced);
    const gasPercentRecovered = gasEUR > 0 ? (gasProduced / gasEUR) * 100 : 0;
    
    return {
      wellId,
      wellName,
      oil: {
        eur: oilEUR,
        produced: oilProduced,
        remaining: oilRemaining,
        percentRecovered: oilPercentRecovered,
      },
      gas: {
        eur: gasEUR,
        produced: gasProduced,
        remaining: gasRemaining,
        percentRecovered: gasPercentRecovered,
      },
      method: 'decline-curve',
      confidence: declineCurve.quality.confidence,
    };
  }
  
  // ============= ROI DASHBOARD =============
  
  /**
   * Calculate comprehensive ROI metrics
   */
  calculateROI(
    wellId: string,
    wellName: string,
    investment: ROIDashboard['investment'],
    historicalRevenue: number,
    historicalOpex: number,
    forecast: DeclineCurveAnalysis,
    oilPrice: number,
    opexPerBarrel: number,
    discountRate: number = 10
  ): ROIDashboard {
    // Calculate forecast revenue and costs
    let forecastRevenue = 0;
    let forecastOpex = 0;
    
    forecast.forecast.forEach(f => {
      const monthlyRevenue = f.rate * 30 * oilPrice;
      const monthlyOpex = f.rate * 30 * opexPerBarrel;
      forecastRevenue += monthlyRevenue;
      forecastOpex += monthlyOpex;
    });
    
    const totalRevenue = historicalRevenue + forecastRevenue;
    const totalOpex = historicalOpex + forecastOpex;
    
    // Build cash flow
    const cashFlow: ROIDashboard['cashFlow'] = [];
    let cumulativeCashFlow = -investment.total;
    
    // Historical cash flow
    cashFlow.push({
      date: new Date(),
      revenue: historicalRevenue,
      costs: historicalOpex,
      netCashFlow: historicalRevenue - historicalOpex,
      cumulativeCashFlow: cumulativeCashFlow + historicalRevenue - historicalOpex,
    });
    
    cumulativeCashFlow = cashFlow[0].cumulativeCashFlow;
    
    // Forecast cash flow
    forecast.forecast.forEach(f => {
      const monthlyRevenue = f.rate * 30 * oilPrice;
      const monthlyOpex = f.rate * 30 * opexPerBarrel;
      const netCashFlow = monthlyRevenue - monthlyOpex;
      cumulativeCashFlow += netCashFlow;
      
      cashFlow.push({
        date: f.date,
        revenue: monthlyRevenue,
        costs: monthlyOpex,
        netCashFlow,
        cumulativeCashFlow,
      });
    });
    
    // Calculate NPV
    const npv = this.calculateNPV(cashFlow, investment.total, discountRate);
    
    // Calculate IRR (simplified - use Newton's method in production)
    const irr = this.calculateIRR(cashFlow, investment.total);
    
    // Calculate ROI
    const roi = investment.total > 0 ? ((totalRevenue - totalOpex - investment.total) / investment.total) * 100 : 0;
    
    // Calculate payback period
    const breakEvenIndex = cashFlow.findIndex(cf => cf.cumulativeCashFlow > 0);
    const breakEvenDate = breakEvenIndex >= 0 ? cashFlow[breakEvenIndex].date : null;
    const paybackMonths = breakEvenIndex >= 0 ? breakEvenIndex : Infinity;
    
    // Profitability Index
    const profitabilityIndex = investment.total > 0 ? (npv + investment.total) / investment.total : 0;
    
    return {
      wellId,
      wellName,
      investment,
      revenue: {
        historical: historicalRevenue,
        forecast: forecastRevenue,
        total: totalRevenue,
      },
      opex: {
        historical: historicalOpex,
        forecast: forecastOpex,
        total: totalOpex,
      },
      cashFlow,
      metrics: {
        npv,
        irr,
        roi,
        paybackMonths,
        breakEvenDate,
        profitabilityIndex,
      },
      discountRate,
    };
  }
  
  /**
   * Calculate Net Present Value
   */
  private calculateNPV(
    cashFlow: Array<{ netCashFlow: number }>,
    initialInvestment: number,
    discountRate: number
  ): number {
    let npv = -initialInvestment;
    
    cashFlow.forEach((cf, i) => {
      const discountFactor = Math.pow(1 + discountRate / 100, i);
      npv += cf.netCashFlow / discountFactor;
    });
    
    return npv;
  }
  
  /**
   * Calculate Internal Rate of Return (simplified)
   */
  private calculateIRR(
    cashFlow: Array<{ netCashFlow: number }>,
    initialInvestment: number
  ): number {
    // Simplified IRR calculation
    // In production, use Newton-Raphson or bisection method
    
    const totalCashFlow = cashFlow.reduce((sum, cf) => sum + cf.netCashFlow, 0);
    const averageReturn = totalCashFlow / cashFlow.length;
    const irr = (averageReturn / initialInvestment) * 100;
    
    return Math.max(0, irr);
  }
  
  // ============= PREDICTIVE MAINTENANCE =============
  
  /**
   * Analyze well condition and predict maintenance needs
   */
  analyzePredictiveMaintenance(
    wellId: string,
    wellName: string,
    currentData: {
      pressure: number;
      production: number;
      runtime: number; // hours
      temperature: number; // F
      vibration: number; // mm/s
      efficiency: number; // %
    },
    historicalIssues: Array<{
      date: Date;
      type: string;
      cost: number;
      downtime: number;
    }>
  ): PredictiveMaintenance {
    // Define factors and their thresholds
    const factors = [
      {
        factor: 'Pressure Drop',
        weight: 0.25,
        value: currentData.pressure,
        threshold: 100, // PSI
        status: this.getStatus(currentData.pressure, 100, 50),
      },
      {
        factor: 'Production Decline',
        weight: 0.20,
        value: currentData.production,
        threshold: 20, // bbl/day
        status: this.getStatus(currentData.production, 20, 10),
      },
      {
        factor: 'Equipment Runtime',
        weight: 0.15,
        value: currentData.runtime,
        threshold: 8000, // hours
        status: this.getStatus(currentData.runtime, 8000, 10000, true),
      },
      {
        factor: 'Temperature',
        weight: 0.15,
        value: currentData.temperature,
        threshold: 180, // F
        status: this.getStatus(currentData.temperature, 180, 200, true),
      },
      {
        factor: 'Vibration',
        weight: 0.15,
        value: currentData.vibration,
        threshold: 4.5, // mm/s
        status: this.getStatus(currentData.vibration, 4.5, 7.0, true),
      },
      {
        factor: 'Efficiency',
        weight: 0.10,
        value: currentData.efficiency,
        threshold: 80, // %
        status: this.getStatus(currentData.efficiency, 80, 60),
      },
    ];
    
    // Calculate risk score (0-100)
    let riskScore = 0;
    factors.forEach(factor => {
      let contribution = 0;
      
      if (factor.status === 'critical') {
        contribution = 100 * factor.weight;
      } else if (factor.status === 'warning') {
        contribution = 60 * factor.weight;
      } else {
        contribution = 20 * factor.weight;
      }
      
      factor.contribution = contribution;
      riskScore += contribution;
    });
    
    // Determine risk level
    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    if (riskScore < 30) riskLevel = 'low';
    else if (riskScore < 60) riskLevel = 'medium';
    else if (riskScore < 80) riskLevel = 'high';
    else riskLevel = 'critical';
    
    // Predict next failure
    const avgTimeBetweenFailures = this.calculateMTBF(historicalIssues);
    const nextFailureDate = avgTimeBetweenFailures > 0
      ? new Date(Date.now() + avgTimeBetweenFailures * 24 * 60 * 60 * 1000)
      : null;
    
    const confidence = historicalIssues.length >= 3 ? 0.8 : 0.5;
    
    // Recommended action
    let recommendedAction = '';
    let estimatedCost = 0;
    
    if (riskLevel === 'critical') {
      recommendedAction = 'Immediate inspection and repair required';
      estimatedCost = 5000;
    } else if (riskLevel === 'high') {
      recommendedAction = 'Schedule maintenance within 1 week';
      estimatedCost = 2500;
    } else if (riskLevel === 'medium') {
      recommendedAction = 'Schedule maintenance within 1 month';
      estimatedCost = 1000;
    } else {
      recommendedAction = 'Continue normal operations';
      estimatedCost = 500;
    }
    
    return {
      wellId,
      wellName,
      riskScore,
      riskLevel,
      factors: factors.map(f => ({
        ...f,
        contribution: f.contribution,
      })),
      predictions: {
        nextFailureDate,
        confidence,
        recommendedAction,
        estimatedCost,
      },
      issues: historicalIssues,
    };
  }
  
  private getStatus(
    value: number,
    warningThreshold: number,
    criticalThreshold: number,
    reverse: boolean = false
  ): 'normal' | 'warning' | 'critical' {
    if (reverse) {
      // Higher is worse
      if (value >= criticalThreshold) return 'critical';
      if (value >= warningThreshold) return 'warning';
      return 'normal';
    } else {
      // Lower is worse
      if (value <= criticalThreshold) return 'critical';
      if (value <= warningThreshold) return 'warning';
      return 'normal';
    }
  }
  
  private calculateMTBF(issues: Array<{ date: Date }>): number {
    if (issues.length < 2) return 0;
    
    const sorted = [...issues].sort((a, b) => a.date.getTime() - b.date.getTime());
    let totalDays = 0;
    
    for (let i = 1; i < sorted.length; i++) {
      const days = (sorted[i].date.getTime() - sorted[i - 1].date.getTime()) / (1000 * 60 * 60 * 24);
      totalDays += days;
    }
    
    return totalDays / (sorted.length - 1);
  }
  
  // ============= WATER CUT ANALYSIS =============
  
  /**
   * Analyze water cut trends and forecast
   */
  analyzeWaterCut(
    wellId: string,
    wellName: string,
    productionData: ProductionDataPoint[]
  ): WaterCutAnalysis {
    // Calculate historical water cut
    const historical = productionData.map(d => {
      const totalLiquid = d.oil + d.water;
      const waterCut = totalLiquid > 0 ? (d.water / totalLiquid) * 100 : 0;
      const oilRate = d.days > 0 ? d.oil / d.days : 0;
      const waterRate = d.days > 0 ? d.water / d.days : 0;
      
      return {
        date: d.date,
        waterCut,
        oilRate,
        waterRate,
      };
    });
    
    // Calculate trend
    const trend = this.calculateWaterCutTrend(historical);
    
    // Forecast 12 months
    const forecast: WaterCutAnalysis['forecast'] = [];
    const lastWaterCut = historical[historical.length - 1].waterCut;
    
    for (let month = 1; month <= 12; month++) {
      const projectedWaterCut = Math.min(100, lastWaterCut + (trend.rate * month));
      const date = new Date(historical[historical.length - 1].date);
      date.setMonth(date.getMonth() + month);
      
      forecast.push({
        date,
        waterCut: projectedWaterCut,
        confidence: Math.max(0, 1 - (month * 0.05)), // Decrease confidence over time
      });
    }
    
    return {
      wellId,
      wellName,
      historical,
      trend,
      forecast,
      thresholds: {
        current: lastWaterCut,
        warning: 80,
        critical: 95,
      },
    };
  }
  
  private calculateWaterCutTrend(
    data: Array<{ date: Date; waterCut: number }>
  ): WaterCutAnalysis['trend'] {
    if (data.length < 2) {
      return {
        direction: 'stable',
        rate: 0,
        projection12Mo: data[0]?.waterCut || 0,
      };
    }
    
    // Simple linear regression
    const n = data.length;
    const x = data.map((_, i) => i);
    const y = data.map(d => d.waterCut);
    
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    
    // Determine direction
    let direction: 'increasing' | 'decreasing' | 'stable';
    if (Math.abs(slope) < 0.5) {
      direction = 'stable';
    } else if (slope > 0) {
      direction = 'increasing';
    } else {
      direction = 'decreasing';
    }
    
    // Calculate monthly rate
    const monthlyRate = slope;
    
    // Project 12 months
    const projection12Mo = Math.min(100, Math.max(0, y[y.length - 1] + monthlyRate * 12));
    
    return {
      direction,
      rate: monthlyRate,
      projection12Mo,
    };
  }
  
  // ============= PRODUCTION FORECASTING =============
  
  /**
   * Forecast production for 24 months
   */
  forecastProduction(
    wellId: string,
    wellName: string,
    declineCurve: DeclineCurveAnalysis,
    assumptions: {
      oilPrice: number;
      gasPrice: number;
      gasOilRatio: number;
      operatingDays: number;
    }
  ): ProductionForecast {
    const forecast: ProductionForecast['forecast'] = [];
    const upperBound: number[] = [];
    const lowerBound: number[] = [];
    
    // Use decline curve forecast
    for (let month = 1; month <= 24; month++) {
      const forecastIndex = Math.min(month - 1, declineCurve.forecast.length - 1);
      const forecastPoint = declineCurve.forecast[forecastIndex];
      
      const date = new Date();
      date.setMonth(date.getMonth() + month);
      
      const dailyRate = forecastPoint.rate;
      const monthlyOil = dailyRate * assumptions.operatingDays;
      const monthlyGas = monthlyOil * assumptions.gasOilRatio;
      const revenue = (monthlyOil * assumptions.oilPrice) + (monthlyGas * assumptions.gasPrice);
      
      forecast.push({
        month,
        date,
        oil: monthlyOil,
        gas: monthlyGas,
        revenue,
      });
      
      // Confidence intervals (±20%)
      upperBound.push(monthlyOil * 1.2);
      lowerBound.push(monthlyOil * 0.8);
    }
    
    return {
      wellId,
      wellName,
      forecast,
      confidence: {
        level: 95,
        upper: upperBound,
        lower: lowerBound,
      },
      assumptions,
    };
  }
}

export const analyticsEngine = new AnalyticsEngine();
